import { z } from 'zod';
import Payments from '~/stores/payments';
import api from '~/utils/api';
import {
  applyCreditMethodToClaimLineItems,
  isExchangeClaim,
  pendingClaimLineItemToCalculatedLineItem,
} from '~/utils/claim-line-item';
import { ClaimType } from '~/utils/constants';
import { toCurrency } from '~/utils/formatters';
import {
  makeCrewClaimWithClaimLineItemsLoader,
  redirect,
} from '~/utils/routing';
import { claimLineItemSchema, CreditResolutionMethod } from '~/utils/schemas';
import { getCreditMethods } from './return-summary-credit-method';
import { getExchangeMethods } from './return-summary-exchange-method';
import { getReturnSummaryParams } from './return-summary-search-params';

// ----------- Refund Methods -----------

type ReturnSummary = NonNullable<
  Awaited<
    ReturnType<typeof api.calculateBeforeClaimSubmission>
  >['returnSummary']
>;

const getOutstandingBalance = (
  total: number,
  refundMethod: CreditResolutionMethod | null,
) => {
  // If the balance is positive, this indicates in favor of the merchant
  if (total > 0) {
    return {
      kind: 'Payment' as const,
      total,
      label: 'Payment due',
    };
  }

  if (total === 0 || !refundMethod) return null;

  return {
    total,
    kind: refundMethod,
    label: refundMethod === 'Refund' ? 'Refund owed' : 'Gift Card owed',
  };
};

const extractLineItemResp = (
  claimLineItems: z.infer<typeof claimLineItemSchema>[],
  summaryLineItems: NonNullable<ReturnSummary['credits']>['lineItems'],
  type: 'Deductions' | 'Return Credit',
) =>
  claimLineItems.map(
    ({
      unitPrice,
      quantity,
      variant,
      originalStoreOrderLineItemId,
      lineItem: { name, optionsFromPlatform: options, imgUrl },
    }) => {
      const match = summaryLineItems.find(
        (cli) =>
          cli.originalStoreOrderLineItemId === originalStoreOrderLineItemId,
      );

      if (!match || type === 'Return Credit')
        return {
          unitPrice,
          quantity,
          name,
          options,
          imgUrl,
        };

      return {
        ...match,
        quantity,
        name: variant?.title,
        options: variant?.options,
        imgUrl: variant?.imgUrl,
      };
    },
  );

export const { loader, useLoaderData: useReturnSummaryLoaderData } =
  makeCrewClaimWithClaimLineItemsLoader(
    async ({ params: { store }, context, request }) => {
      if (!context.crewClaim.returnMethod) {
        return redirect(`/${store}`);
      }

      const { settings, order, crewClaim } = context;
      const { currencyCode } = order;

      const params = getReturnSummaryParams(request.url);
      const creditMethod = getCreditMethods(
        context.settings,
        context.crewClaim.claimLineItems,
        params.creditMethod,
      );
      const exchangeMethod = getExchangeMethods(context, params.exchangeMethod);

      const toCustomerItems = crewClaim.claimLineItems.filter(isExchangeClaim);
      const fromCustomerItems = applyCreditMethodToClaimLineItems(
        crewClaim.claimLineItems,
        creditMethod.selected,
      );

      const { returnSummary } = await api.calculateBeforeClaimSubmission({
        params: { storefrontId: settings.storefrontId },
        body: {
          id: order.id,
          claimType: ClaimType.return,
          calculatedLineItems: fromCustomerItems.map((claim) =>
            pendingClaimLineItemToCalculatedLineItem(claim),
          ),
          exchangeMethod: exchangeMethod.selected,
          returnShipment: crewClaim.returnMethod,
          toCustomerRate: crewClaim.toCustomerRate,
          creditResolutionMethods:
            creditMethod.selected === 'Gift_Card' ? ['Gift_Card'] : [],
        },
      });

      // ! returnSummary should be set here, as we are redirecting from the return review action
      if (!returnSummary) throw new Error('Return Summary not found!');

      const { balance, paymentIntent } = returnSummary;

      if (paymentIntent) {
        Payments.setPaymentIntent(
          paymentIntent.clientSecret,
          paymentIntent.idFromPlatform,
        );
      }

      // the max amount that can be can be charge to the customer
      const authorizedAmount = paymentIntent ? paymentIntent.amount : balance;

      const outstandingBalance = getOutstandingBalance(
        authorizedAmount,
        creditMethod.selected,
      );

      return {
        credits: {
          ...returnSummary.credits,
          lineItems: extractLineItemResp(
            fromCustomerItems,
            returnSummary.credits.lineItems,
            'Return Credit',
          ),
        },
        deductions: {
          ...returnSummary.deductions,
          lineItems: extractLineItemResp(
            toCustomerItems,
            returnSummary.deductions.lineItems,
            'Deductions',
          ),
        },
        exchangeMethod,
        balance,
        creditMethod,
        authorizedAmount,
        paymentIntentSecret: paymentIntent?.clientSecret,
        outstandingBalance,
        currencyFormatter: toCurrency(currencyCode),
      };
    },
  );
